import { supabase } from '../lib/supabase'

/**
 * Clearing the backlog in one press.
 *
 * The first real sweep found 158 open threads across 67 organisations, 133 of
 * them untouched for a fortnight or more. That is not noise — Bloomberg alone
 * has 31 live threads — it is a genuine backlog that had simply never been
 * visible in one place before. But marking it one row at a time is 158
 * presses, and a screen nobody clears is a screen nobody reads.
 *
 * This writes exactly the SAME mark the per-row Done button writes, against
 * each thread's own last message time, so every rule still holds. Above all:
 * **a new message on any of them brings that row straight back.** Nothing is
 * deleted, nothing is hidden for good, and after the first clear the tab shows
 * only what has actually moved since.
 */

const DAYS = 30

/** The cut-off, in days, that the control offers. One month reads as one month. */
export const BACKLOG_DAYS = DAYS

export interface BulkDone {
  batchId: string
  marked: number
}

/** How many a press would clear, so the button can say so before it is pressed. */
export async function countBacklog(): Promise<number> {
  if (!supabase) return 0
  const { data, error } = await supabase.rpc('count_conversations_before', { p_days: DAYS })
  if (error) {
    console.warn(`[supabase] backlog count failed: ${error.message}`)
    return 0
  }
  return Number(data ?? 0)
}

export async function clearBacklog(): Promise<BulkDone | null> {
  if (!supabase) return null
  const { data, error } = await supabase.rpc('mark_conversations_done_before', { p_days: DAYS })
  if (error) {
    console.warn(`[supabase] bulk done failed: ${error.message}`)
    return null
  }
  // The function returns a single row; supabase-js hands it back as an array.
  const row = (Array.isArray(data) ? data[0] : data) as
    | { batch_id?: string; marked?: number }
    | null
  if (!row?.batch_id) return null
  return { batchId: String(row.batch_id), marked: Number(row.marked ?? 0) }
}

/**
 * Undo the whole press as one thing. A hundred rows cleared by accident is not
 * something anyone should have to reopen by hand, which is the only reason
 * the marks carry a batch id at all.
 */
export async function undoBacklog(batchId: string): Promise<boolean> {
  if (!supabase) return false
  const { error } = await supabase.rpc('undo_conversation_batch', { p_batch: batchId })
  if (error) {
    console.warn(`[supabase] undo batch failed: ${error.message}`)
    return false
  }
  return true
}
